/* =============================================================================
   CORE / DISPLAY-CONFIG — KDS + Theken-Display-Konfiguration
   =============================================================================
   v0.30.0 / Session 20 / Phase 16.4 (Jonas-Request P10)
   ----------------------------------------------------------------------------
   Zentrale Quelle für die per-Restaurant konfigurierbaren Display-Settings:
   - Schwellzeiten pro Status für das Ampel-System (grün/gelb/rot)
   - Sichtbarkeit + Reihenfolge der Info-Felder auf einer Kachel

   Daten landen in settings.displayConfig:
     {
       kds: { thresholds: { pending, preparing, ready }, fields: [...] },
       bar: { thresholds: { pending, preparing, ready }, fields: [...] }
     }
============================================================================ */

/** Definition aller verfügbaren Info-Felder. Mehr Felder können später
 *  ergänzt werden — die Reihenfolge im Array entscheidet nur über die
 *  Default-Reihenfolge, nicht über die Render-Reihenfolge. */
export const DISPLAY_FIELDS = [
  { id: 'orderNumber', label: 'Bestell-Nummer',     desc: 'Z.B. #00042' },
  { id: 'waitTime',    label: 'Wartezeit',          desc: 'Minuten seit Eingang' },
  { id: 'customer',    label: 'Kundenname',         desc: 'Aus Bestellung' },
  { id: 'pickup',      label: 'Abholzeit',          desc: 'Gewählte Pickup-Time' },
  { id: 'status',      label: 'Status-Label',       desc: 'Z.B. „In Zubereitung"' },
  { id: 'note',        label: 'Bemerkungen',        desc: 'Kunden-Notiz zur Bestellung, falls da' },
  // S196 (Jonas) — Positions-Kommentar (Cockpit, order_items.notes) je Gericht,
  // getrennt von der Order-Ebenen-„Bemerkung". Beide zeigen nur, wenn gefüllt.
  { id: 'itemNote',    label: 'Notiz (pro Position)', desc: 'Küchen-/Theken-Kommentar je Gericht' }
];

/** Default-Konfiguration. Wird genutzt, wenn settings.displayConfig leer ist
 *  oder Felder fehlen — Migration-freundlich. */
function defaultConfig() {
  return {
    kds: {
      thresholds: { pending: 5, preparing: 10, ready: 15 },
      // Phase 18 (Session 22): Vorlaufzeit für Vorbestellungen. Bei Reservierungs-
      // Vorbestellungen (source='reservation') pausiert der Wartezeit-Timer
      // bis (pickup_time - preorderLeadMin) und startet dann normal hochlaufend
      // mit Ampel. KDS-Default: 30 min (Speisen brauchen Vorlauf).
      preorderLeadMin: 30,
      fields: [
        { id: 'orderNumber', visible: true  },
        { id: 'waitTime',    visible: true  },
        { id: 'customer',    visible: true  },
        { id: 'pickup',      visible: true  },
        { id: 'status',      visible: true  },
        { id: 'note',        visible: true  },
        { id: 'itemNote',    visible: true  }
      ]
    },
    bar: {
      thresholds: { pending: 3, preparing: 6, ready: 10 },
      // Theke-Default: 15 min (Getränke brauchen weniger Vorlauf als Speisen)
      preorderLeadMin: 15,
      fields: [
        { id: 'orderNumber', visible: true  },
        { id: 'waitTime',    visible: true  },
        { id: 'customer',    visible: true  },
        { id: 'pickup',      visible: true  },
        { id: 'status',      visible: true  },
        { id: 'note',        visible: false },
        { id: 'itemNote',    visible: true  }
      ]
    },
    // S62 HF6+HF7 — Liefer-Cockpit-Display (Phase 64+65). Schwellen für die
    // beiden aktiven Bucket-Status der vereinfachten Liefer-Pipeline:
    //   assigned          — Fahrer benannt, wartet auf Küche/Tour-Start
    //   out_for_delivery  — Fahrer auf Tour zum Kunden
    // Pre-Order-Vorlauf (preorderLeadMin) ist die Zeit vor pickup_time, ab
    // der eine Vorbestellung „scharf" geschaltet wird.
    // Hinweis: pending hat keinen Threshold — die Ampel startet erst nach
    // Zuweisung, weil vorher die Verantwortung beim Disponent liegt.
    delivery: {
      thresholds: { assigned: 15, out_for_delivery: 30 },
      preorderLeadMin: 60,
      fields: [
        { id: 'orderNumber', visible: true  },
        { id: 'waitTime',    visible: true  },
        { id: 'customer',    visible: true  },
        { id: 'pickup',      visible: true  },
        { id: 'status',      visible: true  },
        { id: 'note',        visible: true  },
        { id: 'itemNote',    visible: true  }
      ]
    }
  };
}

/** Liest die effektive Display-Config aus den Settings, mit Defaults gemerged.
 *  Stellt sicher, dass alle Felder vorhanden sind, auch wenn ein neueres Feld
 *  in einem alten settings-Eintrag noch nicht steht.
 */
export function getDisplayConfig(display) {
  if (display !== 'kds' && display !== 'bar' && display !== 'delivery') return defaultConfig().kds;

  const state = (typeof window !== 'undefined' && window.App && window.App.storage
                 && window.App.storage._getInternalState && window.App.storage._getInternalState()) || { settings: {} };
  const stored = (state.settings && state.settings.displayConfig && state.settings.displayConfig[display]) || {};
  const defaults = defaultConfig()[display];

  // S62 HF6 — Delivery hat ein anderes Threshold-Schema (assigned/picked_up/
  // out_for_delivery statt pending/preparing/ready). Wir nutzen dieselbe
  // sanitizeThreshold-Logik, mappen aber dynamisch über die Default-Keys.
  const thresholdKeys = Object.keys(defaults.thresholds);
  const thresholds = {};
  for (const k of thresholdKeys) {
    thresholds[k] = sanitizeThreshold(
      stored.thresholds && stored.thresholds[k],
      defaults.thresholds[k]
    );
  }

  // Phase 18 (Session 22): Vorlaufzeit für Vorbestellungen — pro Display einstellbar
  const preorderLeadMin = sanitizeLeadMin(stored.preorderLeadMin, defaults.preorderLeadMin);

  // Felder: alle bekannten DISPLAY_FIELDS müssen vorhanden sein. Stored-Reihenfolge
  // wird respektiert; fehlende Felder hängen unten an (default visible=true).
  const known = new Set(DISPLAY_FIELDS.map(f => f.id));
  const storedFields = Array.isArray(stored.fields) ? stored.fields.filter(f => f && known.has(f.id)) : [];
  const seen = new Set(storedFields.map(f => f.id));
  const merged = storedFields.slice();
  for (const f of DISPLAY_FIELDS) {
    if (!seen.has(f.id)) {
      // Falls displayConfig in den Defaults explizit visible=false setzt
      // (z.B. note für bar), das übernehmen — sonst true.
      const defField = defaults.fields.find(df => df.id === f.id);
      merged.push({ id: f.id, visible: defField ? !!defField.visible : true });
    }
  }

  return { thresholds, preorderLeadMin, fields: merged };
}

/** S196 (Jonas) — ist ein Display-Feld sichtbar? (Single-Source für Nicht-Kachel-
 *  Felder wie `itemNote`, das pro Position rendert und nicht über
 *  renderConfiguredFields läuft). Unbekanntes Feld → sichtbar (fail-open). */
export function isDisplayFieldVisible(display, fieldId) {
  const cfg = getDisplayConfig(display);
  const f = (cfg.fields || []).find(x => x.id === fieldId);
  return f ? !!f.visible : true;
}

function sanitizeLeadMin(v, fallback) {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.min(240, Math.round(n));
}

function sanitizeThreshold(v, fallback) {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.min(120, Math.round(n));
}

/** Speichert eine neue Display-Config zurück. Erhält die andere Sicht
 *  (kds vs. bar) unverändert. */
export async function setDisplayConfig(display, nextConfig) {
  if (display !== 'kds' && display !== 'bar' && display !== 'delivery') return;
  if (typeof window.updateSetting !== 'function') return;
  const state = (window.App && window.App.storage && window.App.storage._getInternalState
                 && window.App.storage._getInternalState()) || { settings: {} };
  const cur = (state.settings && state.settings.displayConfig) || {};
  const next = { ...cur, [display]: nextConfig };
  await window.updateSetting('displayConfig', next);
}

/**
 * Liefert die CSS-Klasse für den Ampel-Zustand: 'wait-green' / 'wait-yellow' /
 * 'wait-red' / 'wait-paused'. Schwellen kommen pro Order-Status aus der Config:
 *   - mode='waiting' (Vorbestellung vor Vorlaufzeit) → 'wait-paused'
 *   - mins < (T_phase / 2)  → grün
 *   - mins < T_phase        → gelb
 *   - mins ≥ T_phase        → rot
 * Wenn der Status nicht in thresholds steht (z.B. confirmed), nehmen wir die
 * pending-Schwelle als Default.
 *
 * Phase 18 (Session 22): mode-Parameter optional — wird vom Helper
 * computeOrderTimer geliefert. Backward-compatible: alte Aufrufe ohne mode
 * verhalten sich wie früher (kein 'wait-paused').
 */
export function colorForOrder(display, order, mins, mode) {
  if (mode === 'waiting') return 'wait-paused';
  const cfg = getDisplayConfig(display);
  // Phase 19 (Session 22): Ampel-Schwelle aus dem Sub-Status für den jeweiligen
  // Channel ziehen (KDS → kitchen_status, Bar → bar_status). Fallback auf
  // Master-Status für Backward-Compat.
  const channel = (display === 'bar') ? 'bar' : 'kitchen';
  const subStatus = (order && (channel === 'kitchen' ? order.kitchenStatus : order.barStatus)) || null;
  const status = subStatus || (order && order.status) || 'pending';
  const t = cfg.thresholds[status] != null ? cfg.thresholds[status] : cfg.thresholds.pending;
  if (mins < t / 2) return 'wait-green';
  if (mins < t)     return 'wait-yellow';
  return 'wait-red';
}

/**
 * Phase 18 (Session 22) — zentrale Timer-Logik für KDS+Bar-Cards.
 *
 * Liefert {mode, mins, label} für eine Order. Zwei Modi:
 *
 *   mode='waiting'  Vorbestellung deren Vorlaufzeit noch nicht erreicht ist.
 *                   Anzeige: "Service in X min" (Countdown zur pickup_time).
 *                   Color: 'wait-paused' (lila/grau, kein Ampel-Stress).
 *
 *   mode='running'  Normaler hochlaufender Wartezeit-Timer.
 *                   Anzeige: "X min" (Zeit seit relevantem Phasen-Start).
 *                   Color: Ampel (grün/gelb/rot) abhängig von Schwelle.
 *
 * Phasen-Start-Logik (running): Timer läuft ab dem aktuellsten Status-
 * Timestamp — das ist die intuitive Küchen-Wahrnehmung "seit ich auf
 * 'Zubereiten' geklickt habe, läuft die Pfanne".
 *   ready     → ready_at
 *   preparing → preparing_at
 *   confirmed → confirmed_at
 *   pending   → created_at (Fallback)
 *
 * Bediener-Override: Wenn ein Bediener vor Erreichen der Vorlaufzeit auf
 * 'Annehmen'/'Zubereiten' klickt, kippt die Order automatisch in
 * mode='running', weil der Status-Timestamp die Lead-Zeit überschreibt.
 *
 * Edge-Cases:
 *   - Walk-In/Takeaway-Order ohne reservation_id → immer running ab Phasen-Start
 *   - Reservation mit pickup_time in Vergangenheit → running (Vorlaufzeit egal)
 *   - leadMin=0 → Timer startet exakt zur pickup_time
 */
export function computeOrderTimer(display, order) {
  const cfg = getDisplayConfig(display);
  const now = Date.now();

  // Phase 19 (Session 22): Channel-aware Phasen-Timer.
  // Wenn die Order Sub-Status für den jeweiligen Channel hat (KDS → kitchen,
  // Bar → bar), nutzen wir den Sub-Status + die Channel-Timestamps. Das
  // entspricht der realen Küchen-Wahrnehmung: "seit ich auf KDS auf
  // 'Zubereiten' geklickt habe, läuft die Pfanne — der Bar-Status ist mir
  // hier egal".
  // Fallback (alte Orders ohne Sub-Status oder Display='other'): Master-Status.
  const channel = (display === 'bar') ? 'bar' : 'kitchen';
  const subStatus = (channel === 'kitchen') ? order.kitchenStatus : order.barStatus;
  const subPreparingAt = (channel === 'kitchen') ? order.kitchenPreparingAt : order.barPreparingAt;
  const subReadyAt = (channel === 'kitchen') ? order.kitchenReadyAt : order.barReadyAt;

  // Phasen-Start: bevorzugt Sub-Status-Timestamp, sonst Master-Timestamp, sonst createdAt
  const status = subStatus || (order && order.status) || 'pending';
  const phaseStartIso =
    (status === 'ready'     && (subReadyAt     || order.readyAt))     ||
    (status === 'preparing' && (subPreparingAt || order.preparingAt)) ||
    (status === 'confirmed' && order.confirmedAt) ||
    order.createdAt;
  const phaseStartTs = phaseStartIso ? new Date(phaseStartIso).getTime() : now;

  // S62 HF10 (Jonas-Direktive) — Lieferung scharf schalten: sobald der Fahrer
  // zugewiesen wurde (delivery_status='assigned' oder höher), wird die Order
  // im KDS/Theke IMMER als „running" angezeigt — auch wenn pickup_time noch
  // in der Zukunft liegt (Pre-Order). Begründung: der Fahrer steht bereit
  // und wartet auf Küche/Theke, also muss jetzt produziert werden, nicht
  // erst zur Vorlaufzeit. Cancelled/no_show schalten nichts scharf.
  const isDeliveryAssigned = order.serviceType === 'delivery'
    && order.deliveryStatus && order.deliveryStatus !== 'pending' && order.deliveryStatus !== 'cancelled';

  // Phase 19.1 (Session 22 Funktionstest): Vorlaufzeit gilt für ALLE Orders
  // mit pickup_time in der Zukunft, nicht nur für Reservierungs-Vorbestellungen.
  // Eine Customer-Takeaway-Order für 16:00 (abgesetzt um 11:30) ist genauso
  // eine Vorbestellung wie eine Reservierungs-Übernahme — die Küche/Theke
  // soll erst zur Vorlaufzeit lostakten. Vorher Bug: source='online' fiel durch.
  const pickupTs = parsePickupTimestamp(order.pickupDate, order.pickupTime);
  if (pickupTs != null) {
    const leadMs = (cfg.preorderLeadMin || 0) * 60 * 1000;
    const runStartTs = pickupTs - leadMs;
    if (now < runStartTs && !isDeliveryAssigned) {
      // WAITING: noch vor Vorlaufzeit — Countdown zur Service-Zeit
      const minsUntilService = Math.ceil((pickupTs - now) / 60000);
      return {
        mode: 'waiting',
        mins: minsUntilService,
        label: 'Service in ' + minsUntilService + ' min'
      };
    }
    // S62 HF10 — bei delivery+assigned: Timer läuft ab dem späteren von
    // {phaseStart, delivery_assigned_at} statt ab runStartTs (sonst hätte
    // eine in 3h fällige Pre-Order beim Zuweisen sofort „180 min Wartezeit"
    // — das wäre verwirrend). So zeigt die Card eine ehrliche Wartezeit
    // ab dem Moment, in dem der Disponent „Zuweisen" geklickt hat.
    if (isDeliveryAssigned && now < runStartTs) {
      const assignTs = order.deliveryAssignedAt ? new Date(order.deliveryAssignedAt).getTime() : now;
      const effectiveStart = Math.max(assignTs, phaseStartTs);
      const mins = Math.max(0, Math.floor((now - effectiveStart) / 60000));
      return { mode: 'running', mins, label: mins + ' min' };
    }
    // Vorlaufzeit erreicht ODER Bediener hat schon weiter gestellt
    // (preparing/ready). Timer läuft ab max(runStartTs, phaseStartTs) —
    // wer später kam, gewinnt. So überschreibt ein früher Klick auf
    // 'Zubereiten' die Lead-Time, und nach Erreichen der Lead-Time
    // startet sie automatisch.
    const effectiveStart = Math.max(runStartTs, phaseStartTs);
    const mins = Math.max(0, Math.floor((now - effectiveStart) / 60000));
    return { mode: 'running', mins, label: mins + ' min' };
  }

  // Fallback: keine pickup_time → einfacher Hochlauf-Timer ab Phasen-Start
  const mins = Math.max(0, Math.floor((now - phaseStartTs) / 60000));
  return { mode: 'running', mins, label: mins + ' min' };
}

/**
 * Parst pickup_date + pickup_time in einen Millisekunden-Timestamp.
 * Annahme: pickup_time ist Wand-Uhr in der Restaurant-TZ (= Browser-TZ heute,
 * bis docs/timezone-strategy.md vollständig in Code abgebildet ist).
 * Liefert null bei ungültigen Werten.
 */
function parsePickupTimestamp(pickupDate, pickupTime) {
  if (!pickupDate || !pickupTime) return null;
  const tStr = String(pickupTime).slice(0, 5); // 'HH:MM'
  const m = /^(\d{2}):(\d{2})$/.exec(tStr);
  if (!m) return null;
  const dateStr = String(pickupDate); // 'YYYY-MM-DD'
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!dm) return null;
  const d = new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), Number(m[1]), Number(m[2]), 0, 0);
  const ts = d.getTime();
  return Number.isFinite(ts) ? ts : null;
}

// -----------------------------------------------------------------------------
// Drink-Detection — geteilt zwischen KDS (negativer Filter) und Bar (positiv).
// Ein Item gilt als Getränk, wenn die Kategorie 'Getränke'/'drink' enthält,
// das verlinkte Menu-Item diese Kategorie hat ODER der Name auf ein Getränk
// hinweist (Heuristik).
// -----------------------------------------------------------------------------

const DRINK_NAME_REGEX = /(cola|fanta|sprite|pepsi|spezi|bier|pils|hellbier|weizen|radler|wein|sekt|prosecco|spritz|aperol|wasser|sprudel|still|schorle|saft|cola[a-z]*|limo|eistee|tee|kaffee|espresso|cappuccino|latte|milch|cocktail|gin|tonic|whisk|vodka|rum|mojito|caipi|softdrink|getr[äa]nk)/i;

export function isDrinkItem(item) {
  if (!item) return false;
  const cat = String(
    item.categorySnapshot ||
    item.category_snapshot ||
    item.category ||
    item.menuCategory ||
    ''
  ).toLowerCase();
  if (cat.includes('getränk') || cat.includes('drink')) return true;
  // Menu-Item-Lookup
  const state = (typeof window !== 'undefined' && window.App && window.App.storage
                 && window.App.storage._getInternalState && window.App.storage._getInternalState()) || {};
  const menu = state.menu || state.menuItems || (state.settings && state.settings.menuItems) || [];
  if (Array.isArray(menu)) {
    const mi = menu.find(m => m.id === item.menuItemId || m.id === item.menu_item_id);
    if (mi && mi.category) {
      const mc = String(mi.category).toLowerCase();
      if (mc.includes('getränk') || mc.includes('drink')) return true;
    }
  }
  // Heuristik auf den Namen
  const name = String(item.name || item.nameSnapshot || item.name_snapshot || item.title || '');
  if (DRINK_NAME_REGEX.test(name)) return true;
  return false;
}

/**
 * S171-Hotfix — Darf an einer KDS-Position der „Bauanleitung"-Hammer (🔨)
 * erscheinen? Gate über die KATEGORIE (zusammenstellbares Hauptgericht:
 * Burger/Bowls/Salate), NICHT über buildStepsSync — der A2-/Bauanleitungs-Cache
 * ist im KDS oft nicht geladen (liefert null → Hammer fiele an ALLEN Mains weg).
 * Beilage/Getränk/Optionen → kein Hammer. Pure (nur String-Match), testbar.
 * `category` = Ergebnis von detectCategory() / item.category.
 */
export function kdsCategoryAllowsBuild(category) {
  const c = String(category || '').toLowerCase();
  if (!c) return false;
  return c.includes('burger') || c.includes('bowl') || c.includes('salat') || c.includes('salad');
}

// Backward-Compat: window-Zugriff für inline-Handler in Settings-UI
if (typeof window !== 'undefined') {
  window.DisplayConfig = {
    DISPLAY_FIELDS,
    getDisplayConfig,
    isDisplayFieldVisible,
    setDisplayConfig,
    colorForOrder,
    computeOrderTimer,
    isDrinkItem,
    kdsCategoryAllowsBuild
  };
}
